import {
  FluidFormElementState,
  FluidFormElementTranslationOptions,
  FluidKeyedObject,
  FluidOption,
} from '@lmig/fluid-core';
import { h } from '@stencil/core';

import {
  TranslatableFluidOption,
  translateOption,
  translateWithFallback,
} from '../../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import {
  groupOptions,
  TranslatedGroup,
} from '../../../utils/forms/form-method.utils';

export type FluidSelectTranslationOptions =
  FluidFormElementTranslationOptions & {
    groups: FluidKeyedObject<FluidFormElementTranslationOptions>;
  };

/**
 * Renders a set of ungrouped <option>'s
 * @param props
 * @constructor
 */
export const SingleLevelOptions = (props: {
  options: FluidOption[];
  store: FluidI18nStore;
  fluidTranslate?: FluidSelectTranslationOptions;
  elementState: FluidFormElementState;
}) => {
  const { options, elementState, fluidTranslate, store } = props;
  return options?.map((option: FluidOption) => {
    return (
      <Option
        option={option}
        elementState={elementState}
        fluidTranslate={fluidTranslate}
        store={store}
      />
    );
  });
};

/**
 * Groups and renders <optgroup>'s for any grouped options, before rendering single <option>'s for any
 * options which do not have a group.
 * @param props
 * @constructor
 */
export const GroupedOptions = (props: {
  options: FluidOption[];
  fluidTranslate?: FluidSelectTranslationOptions;
  store: FluidI18nStore;
  elementState: FluidFormElementState;
}) => {
  const { options, elementState, fluidTranslate, store } = props;
  const groups = groupOptions(options);
  const ungroupedOptions = groups['undefined'];
  delete groups['undefined'];

  return [
    ...Object.keys(groups).map((groupId: string) => (
      <OptionGroup
        group={groups[groupId]}
        store={store}
        fluidTranslate={fluidTranslate}
        elementState={elementState}
      />
    )),
    ungroupedOptions?.options.map((option: FluidOption) => (
      <Option
        option={option}
        elementState={elementState}
        fluidTranslate={fluidTranslate}
        store={store}
      />
    )),
  ];
};

/**
 * Renders a translatable option group.
 * @param props
 * @constructor
 */
export const OptionGroup = (props: {
  group: TranslatedGroup;
  store: FluidI18nStore;
  fluidTranslate: FluidSelectTranslationOptions;
  elementState: FluidFormElementState;
}) => {
  const { group, store, fluidTranslate, elementState } = props;
  const { groupId } = group;
  const translationKey = fluidTranslate?.groups?.[groupId]?.label;
  return (
    <optgroup label={translateWithFallback(store, translationKey, group.label)}>
      {group.options.map((option: FluidOption) => (
        <Option
          option={option}
          elementState={elementState}
          fluidTranslate={fluidTranslate}
          store={store}
        />
      ))}
    </optgroup>
  );
};

/**
 * Renders a single select <option> element
 * @param props
 * @constructor
 */
export const Option = (props: {
  option: TranslatableFluidOption;
  fluidTranslate: FluidFormElementTranslationOptions;
  store: FluidI18nStore;
  elementState: FluidFormElementState;
}) => {
  const { option, elementState, fluidTranslate, store } = props;
  return (
    <option
      selected={option.value === elementState.value}
      value={option.value}
      disabled={option.disabled}>
      {translateOption(option, store, fluidTranslate)}
    </option>
  );
};
